import { useFormValidation } from '@react-aria/form';
import { useFormReset, useObjectRef } from '@react-aria/utils';
import { privateValidationStateProp, useFormValidationState } from '@react-stately/form';
import { useControlledState } from '@react-stately/utils';
import clsx from 'clsx';
import { type ReactNode, useContext, useRef, useState, type Ref } from 'react';
import {
  TextContext as RACTextContext,
  ToggleButton as RACToggleButton,
  type ToggleButtonProps as RACToggleButtonProps,
  ToggleGroupStateContext as RACToggleGroupStateContext,
  useSlottedContext,
} from 'react-aria-components';
import type { BaseButtonProps } from '../button/types.js';
import type { AriaLabelingProps, CommonProps } from '../types.js';
import { ToggleButtonGroupContext } from './context.js';

export interface BaseToggleButtonProps
  extends
    Omit<CommonProps, 'id'>,
    AriaLabelingProps,
    Pick<BaseButtonProps, 'size' | 'fullWidth'>,
    Pick<
      RACToggleButtonProps,
      | 'defaultSelected'
      | 'isSelected'
      | 'onPress'
      | 'onHoverStart'
      | 'onHoverEnd'
      | 'onChange'
      | 'isDisabled'
      | 'autoFocus'
    > {
  /**
   * The `<form>` element to associate the input with.
   * The value of this attribute must be the id of a `<form>` in the same document.
   * See [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/input#form).
   */
  form?: string;
  /**
   * The name of the input element, used when submitting an HTML form.
   * See [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/input#name).
   */
  name?: string;
  /**
   * The value of the input element, used when submitting an HTML form.
   * See [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/input#value).
   *
   * When used in a `ToggleButtonGroup`, `value` serves as an identifier for this button, and has to be unique across all buttons in the group.
   */
  value?: string;
}

interface InternalToggleButtonProps extends BaseToggleButtonProps {
  children?: ReactNode;
  buttonRef?: Ref<HTMLButtonElement>;
}

/** @internal */
export function InternalToggleButton({
  buttonRef: ref,
  children,
  size = 'medium',
  isDisabled,
  fullWidth,
  UNSAFE_className,
  UNSAFE_style,
  form,
  name,
  value,
  isSelected: consumerIsSelected,
  defaultSelected: consumerDefaultSelected,
  onChange,
  ...props
}: InternalToggleButtonProps) {
  // State

  const toggleButtonGroupContext = useContext(ToggleButtonGroupContext);
  const toggleGroupState = useContext(RACToggleGroupStateContext);
  const descriptionProps = useSlottedContext(RACTextContext, 'description');
  const errorMessageProps = useSlottedContext(RACTextContext, 'errorMessage');

  const validationState = useFormValidationState({
    // If part of a group, validation is handled at group level
    name: toggleButtonGroupContext ? undefined : name,
    value,
    validationBehavior: 'native',
    [privateValidationStateProp]: toggleButtonGroupContext?.validationState ?? undefined,
  });

  const [isSelected, setIsSelected] = useControlledState(
    toggleGroupState && value ? toggleGroupState.selectedKeys.has(value) : consumerIsSelected,
    consumerDefaultSelected ?? false,
    (newIsSelected) => {
      onChange?.(newIsSelected);

      if (toggleGroupState && value) {
        toggleGroupState.setSelected(value, newIsSelected);
      }

      validationState.commitValidation();
    },
  );

  // Validation

  const buttonRef = useObjectRef(ref);
  const formInputRef = useRef<HTMLInputElement>(null);
  const formInputName = toggleButtonGroupContext?.name ?? name;

  const [initialSelected] = useState(isSelected);
  useFormReset(formInputRef, consumerDefaultSelected ?? initialSelected, setIsSelected);

  useFormValidation(
    {
      focus: () => buttonRef.current?.focus(),
      validationBehavior: 'native',
    },
    validationState,
    formInputRef,
  );

  return (
    <>
      <RACToggleButton
        {...props}
        ref={buttonRef}
        className={clsx('cim-toggle-button', UNSAFE_className)}
        style={UNSAFE_style}
        id={toggleButtonGroupContext ? value : undefined}
        isDisabled={isDisabled}
        isSelected={isSelected}
        onChange={setIsSelected}
        aria-describedby={
          [
            props['aria-describedby'],
            toggleButtonGroupContext?.isInvalid ? errorMessageProps?.id : null,
            descriptionProps?.id,
          ]
            .filter(Boolean)
            .join(' ') || undefined
        }
        data-variant="secondary"
        data-tone="base"
        data-size={size}
        data-full-width={fullWidth || undefined}
      >
        {children}
      </RACToggleButton>

      {formInputName && (
        <input
          ref={formInputRef}
          style={{ display: 'none' }}
          type="checkbox"
          form={toggleButtonGroupContext?.form ?? form}
          name={formInputName}
          checked={isSelected}
          value={value}
          required={toggleButtonGroupContext?.isRequired}
          aria-invalid={
            toggleButtonGroupContext?.validationState.displayValidation.isInvalid ??
            validationState.displayValidation.isInvalid ??
            undefined
          }
          onChange={() => {}}
        />
      )}
    </>
  );
}
